// components/common/PaymentView/PaymentView.tsx
//
// Self-contained payment step. Consolidates the former Summary / Spinner /
// PaymentStatus components into one file. External behavior is unchanged:
// it drives the order through Order.getPaywallUrl() → Stripe redirect →
// Order.getStatus() polling, and reads the `result` URL param on return.
import { useState, useEffect, useCallback } from 'react';
import Button from '../Button/Button';
import Loading from '../Loading/Loading';
import { Order } from '../../../Entities';
import styles from './PaymentView.module.css';

type PaymentState = 'idle' | 'waiting_redirect' | 'waiting_verification' | 'success' | 'error';

interface PaymentViewProps {
  order?: Order | null;
  formData: any;
  title?: string;
  subtitle?: string;
}

const POLL_INTERVAL_MS = 3000;

const removeUrlParam = (key: string): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete(key);
  window.history.replaceState({}, document.title, url.toString());
};

// ── Presentational pieces ─────────────────────────────────────

const OrderSummary = ({ order, formData, title, subtitle, onPay }: {
  order: Order;
  formData: any;
  title?: string;
  subtitle?: string;
  onPay: () => void;
}) => {
  const onFreeTrial = !formData.user?.used_free_trial && formData.selectedTier?.free_trial_days !== 0;
  const trialDays = formData.selectedTier?.free_trial_days;
  const dueNow = onFreeTrial ? 0 : order.amount;

  return (
    <>
      {title && <h2 className={styles.title}>{title}</h2>}
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

      <div className={styles.summaryBox}>
        <h3 className={styles.orderNo}>Objednávka číslo: {order.id}</h3>
        <div className={styles.orderItem}>
          <span>{formData.selectedTier?.name}</span>
          <span>
            {onFreeTrial ? `zdarma na ${trialDays} dny/dní, poté ${order.amount} Kč/měsíc` : `${order.amount} Kč/měsíc`}
          </span>
        </div>
      </div>

      <Button variant="primary" onClick={onPay} className={styles.payButton} fullWidth>
        {`Zaplatit přes Stripe - ${dueNow} Kč`}
      </Button>
      <p className={styles.note}>Po kliknutí budete přesměrováni na zabezpečenou platební bránu Stripe</p>
    </>
  );
};

const PaymentSpinner = ({ text }: { text: string }) => (
  <div className={styles.spinnerContainer}>
    <div className={styles.spinner} />
    <p className={styles.spinnerText}>{text}</p>
  </div>
);

const PaymentResult = ({ status, message, onRetry }: {
  status: 'success' | 'error';
  message: string;
  onRetry: () => void;
}) => {
  const isSuccess = status === 'success';
  return (
    <div className={`${styles.result} ${isSuccess ? styles.success : styles.error}`}>
      <div className={styles.resultIcon}>{isSuccess ? '✅' : '❌'}</div>
      <h2>{isSuccess ? 'Platba úspěšná!' : 'Chyba při platbě'}</h2>
      <p>{message}</p>
      {isSuccess ? (
        <Button variant="primary" onClick={() => (window.location.href = '/dashboard')} fullWidth>
          Přejít na Dashboard
        </Button>
      ) : (
        <Button variant="secondary" onClick={onRetry} fullWidth>
          Zkusit znovu
        </Button>
      )}
    </div>
  );
};

// ── Container ─────────────────────────────────────────────────

const PaymentView = ({ order, formData, title, subtitle }: PaymentViewProps) => {
  const [paymentState, setPaymentState] = useState<PaymentState>('idle');
  const [errorReason, setErrorReason] = useState('');

  // Handle the redirect back from the payment gateway.
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('result');
    removeUrlParam('result');
    if (result === 'error') {
      setErrorReason('Platba byla zrušena.');
      setPaymentState('error');
    } else if (result === 'success') {
      setPaymentState('waiting_verification');
    }
  }, []);

  // Poll order status until it resolves.
  useEffect(() => {
    if (paymentState !== 'waiting_verification') return;

    const interval = setInterval(async () => {
      try {
        const response = await Order.getStatus();
        if (response.order_status === 'active' || response.order_status === 'trialing') {
          clearInterval(interval);
          localStorage.removeItem('tierId');
          setPaymentState('success');
        } else if (response.order_status === 'canceled') {
          clearInterval(interval);
          setErrorReason('Platba byla zrušena.');
          setPaymentState('error');
        }
      } catch (error) {
        clearInterval(interval);
        setErrorReason('Chyba při ověřování platby.');
        setPaymentState('error');
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [paymentState]);

  const handlePayment = useCallback(async () => {
    setPaymentState('waiting_redirect');
    try {
      const paywallUrl = await Order.getPaywallUrl();
      window.location.replace(paywallUrl);
    } catch (error) {
      console.log(error);
      setErrorReason('Došlo k chybě při zahájení platby.');
      setPaymentState('error');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const renderState = () => {
    if (!order && paymentState === 'idle') {
      return (
        <div className={styles.loadingOrder}>
          <Loading maxHeight={false} />
        </div>
      );
    }
    switch (paymentState) {
      case 'idle':
        return <OrderSummary order={order as Order} formData={formData} title={title} subtitle={subtitle} onPay={handlePayment} />;
      case 'waiting_verification':
        return <PaymentSpinner text="Ověřujeme platbu, prosím čekejte..." />;
      case 'waiting_redirect':
        return <PaymentSpinner text="Přesměrováváme Vás na platební bránu" />;
      case 'success':
        return <PaymentResult status="success" message="Vaše platba proběhla úspěšně!" onRetry={handlePayment} />;
      case 'error':
        return <PaymentResult status="error" message={errorReason} onRetry={handlePayment} />;
      default:
        return null;
    }
  };

  return <div className={styles.paymentStep}>{renderState()}</div>;
};

export default PaymentView;
